package xim.poc.game.configuration.v0.tactics

import xim.poc.ActionTargetFilter
import xim.poc.audio.AudioManager
import xim.poc.audio.SystemSound
import xim.poc.game.ActorState
import xim.poc.game.ActorStateManager
import xim.poc.game.GameEngine
import xim.poc.game.UiStateHelper
import xim.poc.game.configuration.ApplierSuccessResult
import xim.poc.game.configuration.SkillApplierRunner
import xim.poc.game.configuration.SkillTargetEvaluator
import xim.poc.game.configuration.constants.SkillId
import xim.poc.game.configuration.v0.CombatBonusAggregator
import xim.resource.AoeType

class TacticsBrowseContext(val ui: TacticsUi, val turn: TacticsMatchUnitTurn): TacticsUiContext {

    override fun onActivated() {
        turn.actionBuilder.reset()
    }

    override fun handleEnter() {
        val tower = ui.match.getHighlightedTower()
        if (tower != null) {
            ui.activateContext(TacticsTowerContext(ui))
            AudioManager.playSystemSoundEffect(SystemSound.MenuSelect)
            return
        }

        val unit = ui.match.getHighlightedUnit() ?: return handleExit()

        if (unit != ui.match.getCurrentTurnUnit()) {
            AudioManager.playSystemSoundEffect(SystemSound.MenuSelect)
            ui.activateContext(TacticsSkillSelect(ui, turn, unit))
            return
        }

        turn.actionBuilder.actorId = unit.id
        AudioManager.playSystemSoundEffect(SystemSound.TargetConfirm)
        ui.activateContext(TacticsMoveSelectContext(ui, turn))
    }

    override fun handleExit() {
        if (ui.match.isSetupPhase()) {
            return ui.match.setCursor(PlayerPrepPosition)
        }

        val current = ui.match.getCurrentTurnUnit() ?: return
        ui.match.setCursor(current.getCursorPosition())
    }

    override fun applyPanelHighlights() {
    }

    override fun isBoardOnly(): Boolean {
        return false
    }

}

class TacticsMoveSelectContext(val ui: TacticsUi, val turn: TacticsMatchUnitTurn): TacticsUiContext {

    private val movementPanels = ui.match.getMovementPositions(turn.unit).toSet()

    override fun onActivated() {
        turn.actionBuilder.destination = null
        syncPosition()
    }

    override fun handleEnter() {
        syncPosition()

        val cursorPosition = ui.match.cursorPosition
        if (cursorPosition !is PanelPosition || !movementPanels.contains(cursorPosition)) {
            AudioManager.playSystemSoundEffect(SystemSound.Invalid)
            return
        }

        AudioManager.playSystemSoundEffect(SystemSound.MenuSelect)
        turn.actionBuilder.destination = cursorPosition
        turn.unit.setMovementCommand(cursorPosition.toWorldSpace())

        ui.activateContext(TacticsSkillSelect(ui, turn, turn.unit))
    }

    override fun handleExit() {
        AudioManager.playSystemSoundEffect(SystemSound.MenuClose)
        ui.activateContext(TacticsBrowseContext(ui, turn))
    }

    override fun applyPanelHighlights() {
        movementPanels.map { toZonePanel(it) }.forEach { it.mask = ui.highlight(Highlight.blueHighlight) }
    }

    private fun syncPosition() {
        turn.unit.clearMovementCommand()
        turn.unit.position.copyFrom(turn.unit.getCursorPosition().toWorldSpace())
    }

}

class TacticsSkillSelect(val ui: TacticsUi, val turn: TacticsMatchUnitTurn, val unit: ActorState): TacticsUiContext {

    private val subUi = TacticsSkillSelectUi(this, ui.match, unit)

    override fun onActivated() {
        turn.actionBuilder.skill = null
        subUi.push()
    }

    override fun handleEnter() {
        val choice = subUi.getSelectedAbility() ?: return

        when (choice) {
            WaitChoice -> {
                if (!TacticsState.shouldConfirmWait()) {
                    UiStateHelper.popState(SystemSound.MenuSelect)
                    turn.submitWait()
                    return
                }

                AudioManager.playSystemSoundEffect(SystemSound.MenuSelect)
                UiStateHelper.pushYesNoMenu(
                    text = "Pass ${turn.unit.name}'s turn?",
                    onYes = { UiStateHelper.popTo(ui.uiState, SystemSound.MenuSelect); turn.submitWait() },
                    onNo = { UiStateHelper.popState(SystemSound.MenuClose) },
                    enableCursorMovementKeys = true,
                )
            }
            is SkillChoice -> {
                UiStateHelper.popState(SystemSound.MenuSelect)
                turn.actionBuilder.skill = choice
                ui.activateContext(TacticsSkillTargetSelect(ui, turn, choice.skillId))
            }
            is PositionChoice -> {
                UiStateHelper.popState(SystemSound.MenuSelect)
                turn.actionBuilder.skill = choice
                ui.activateContext(TacticsPositioningTargetSelect(ui, turn, choice.positionSkill))
            }
        }
    }

    override fun handleExit() {
        if (ui.match.getCurrentTurnUnit()?.id == unit.id) {
            ui.activateContext(TacticsMoveSelectContext(ui, turn))
        } else {
            ui.activateContext(TacticsBrowseContext(ui, turn))
        }
    }

    fun getHoveredSkill(): SkillSelectChoice? {
        return subUi.getSelectedAbility()
    }

    override fun applyPanelHighlights() {
        TacticsSkillSelectHighlighter.applyPanelHighlights(ui, subUi, unit)
    }
}

class TacticsSkillTargetSelect(val ui: TacticsUi, val turn: TacticsMatchUnitTurn, val skill: SkillId): TacticsUiContext {

    private val targetPanels = ui.match.getTargets(turn.unit, skill)

    override fun onActivated() {
        turn.actionBuilder.targetId = null
    }

    override fun handleEnter() {
        val cursorPosition = ui.match.cursorPosition
        if (cursorPosition !is PanelPosition || !targetPanels.contains(cursorPosition)) {
            AudioManager.playSystemSoundEffect(SystemSound.Invalid)
            return
        }

        val targetState = ui.match.getUnit(cursorPosition)
        if (targetState == null || !GameEngine.canBeginSkillOnTarget(turn.unit, targetState, skill)) {
            AudioManager.playSystemSoundEffect(SystemSound.Invalid)
            return
        }

        AudioManager.playSystemSoundEffect(SystemSound.TargetConfirm)
        turn.actionBuilder.targetId = targetState.id
        turn.submitAction()

        ui.match.setCursor(turn.unit.getCursorPosition())
    }

    override fun handleExit() {
        AudioManager.playSystemSoundEffect(SystemSound.MenuClose)
        ui.activateContext(TacticsSkillSelect(ui, turn, turn.unit))
    }

    override fun applyPanelHighlights() {
        turn.unit.faceToward(ui.match.cursorPosition.toWorldSpace())

        for (panel in targetPanels) {
            val targetId = ui.match.getUnit(panel)?.id ?: continue
            val target = ActorStateManager[targetId] ?: continue

            val highlight = if (ActionTargetFilter.areEnemies(turn.unit, target)) {
                Highlight.redHighlight
            } else {
                Highlight.greenHighlight
            }

            val zonePanel = toZonePanel(panel)
            zonePanel.mask = ui.highlight(highlight)
        }

        if (!targetPanels.contains(ui.match.cursorPosition)) { return }

        val target = ui.match.getHighlightedUnit() ?: return
        val info = TacticsSkills[skill]

        CombatBonusAggregator.shallowBonusScope(turn.unit) {
            it.criticalHitRate -= 1000
            val targets = SkillTargetEvaluator.evaluateTargets(skill, turn.unit, target)
            val result = SkillApplierRunner.applySkill(skill, info.skillApplier, turn.unit, targets)
            if (result is ApplierSuccessResult) { ui.skillSpeculation = result.events }
        }

        val panels = when (info.aoeType) {
            AoeType.Target -> {
                val center = ui.match.getUnitPosition(target) as? PanelPosition ?: return
                ui.match.neighborsOf(center, info.aoeRange)
            }
            AoeType.Source -> {
                val center = ui.match.getUnitPosition(turn.unit) as? PanelPosition ?: return
                ui.match.neighborsOf(center, info.aoeRange)
            }
            AoeType.Cone -> {
                getConalTargetPanels(target, info)
            }
            AoeType.Line -> {
                getLineTargetPanels(target, info)
            }
            AoeType.None -> return
        }

        panels.filter { it != ui.match.getUnitPosition(target) }
            .map { toZonePanel(it) }
            .forEach { it.mask = ui.highlight(Highlight.yellowHighlight) }
    }

    private fun getConalTargetPanels(target: ActorState, info: TacticsSkillConfig): List<PanelPosition> {
        val sourcePosition = ui.match.getUnitPosition(turn.unit) as? PanelPosition ?: return emptyList()
        val targetPosition = ui.match.getUnitPosition(target) as? PanelPosition ?: return emptyList()

        val dX = targetPosition.x - sourcePosition.x
        val dY = targetPosition.y - sourcePosition.y

        val panels = ArrayList<PanelPosition>()
        panels += targetPosition

        if (dX != 0) {
            for (i in 1 until info.aoeRange) {
                for (j in -i .. i) {
                    panels += PanelPosition.maybe(targetPosition.x + dX * i, targetPosition.y + j) ?: continue
                }
            }
        } else {
            for (i in 1 until info.aoeRange) {
                for (j in -i .. i) {
                    panels += PanelPosition.maybe(targetPosition.x + j, targetPosition.y + dY * i) ?: continue
                }
            }
        }

        return panels
    }

    private fun getLineTargetPanels(target: ActorState, info: TacticsSkillConfig): List<PanelPosition> {
        val sourcePosition = ui.match.getUnitPosition(turn.unit) as? PanelPosition ?: return emptyList()
        val targetPosition = ui.match.getUnitPosition(target) as? PanelPosition ?: return emptyList()

        val dX = targetPosition.x - sourcePosition.x
        val dY = targetPosition.y - sourcePosition.y

        val panels = ArrayList<PanelPosition>()
        panels += targetPosition

        if (dX != 0) {
            for (i in 1 until info.aoeRange) {
                panels += PanelPosition.maybe(targetPosition.x + dX * i, targetPosition.y) ?: continue
            }
        } else {
            for (i in 1 until info.aoeRange) {
                panels += PanelPosition.maybe(targetPosition.x, targetPosition.y + dY * i) ?: continue
            }
        }

        return panels
    }


}

class TacticsPositioningTargetSelect(val ui: TacticsUi, val turn: TacticsMatchUnitTurn, val skill: TacticsPositionSkill): TacticsUiContext {

    override fun onActivated() {
        turn.actionBuilder.targetId = null
    }

    override fun handleEnter() {
        val cursorPosition = ui.match.cursorPosition
        if (cursorPosition !is PanelPosition) {
            AudioManager.playSystemSoundEffect(SystemSound.Invalid)
            return
        }

        val targetState = ui.match.getUnit(cursorPosition)
        if (targetState == null || !skill.isValid(ui.match, turn.unit, targetState)) {
            AudioManager.playSystemSoundEffect(SystemSound.Invalid)
            return
        }

        AudioManager.playSystemSoundEffect(SystemSound.TargetConfirm)
        turn.actionBuilder.targetId = targetState.id
        turn.submitPositionSkill()

        ui.match.setCursor(turn.unit.getCursorPosition())
    }

    override fun handleExit() {
        AudioManager.playSystemSoundEffect(SystemSound.MenuClose)
        ui.activateContext(TacticsSkillSelect(ui, turn, turn.unit))
    }

    override fun applyPanelHighlights() {
        turn.unit.faceToward(ui.match.cursorPosition.toWorldSpace())

        val center = ui.match.getUnitPosition(turn.unit) as? PanelPosition ?: return
        val panels = ui.match.neighborsOf(center, 1)

        panels.filter {
            val target = ui.match.getUnit(it)
            target != null && skill.isValid(ui.match, turn.unit, target)
        }.map { toZonePanel(it) }.forEach { it.mask = ui.highlight(Highlight.greenHighlight) }
    }

}
